// The SEI module's API (Plan 6 Task 7 item 6; DECISIONS "SEI in v1"): SEI is its own view, shown only to subscriptions
// with the SEI module — the titular's processes on SEI (its mining processes and the administrative ones attributed to
// it), each process's documents and andamentos as published, and a subscription's answer on a possible homonym.
import { z } from 'zod';
import { Id, IsoDate } from './accounts.ts';

export const SeiVerdict = z.enum(['mine', 'not_mine']);

export const SeiDocumentView = z.object({
  id: Id,
  protocolo: z.string(),
  docType: z.string().nullable(),
  title: z.string().nullable(),
  docDate: IsoDate.nullable(),
  unit: z.string().nullable(),
  restricted: z.boolean(),
  /** SEI's own link to the document (sei.anm.gov.br). */
  link: z.string().nullable(),
  /** A deadline-prone type by an ANM unit (P38): "revisar — pode gerar prazo". */
  review: z.boolean(),
  /** SCM/DOU events this document is linked to (ofício number + year). */
  events: z.array(z.object({ eventId: Id, processId: Id, occurredOn: IsoDate.nullable(), description: z.string().nullable() })),
});

export const SeiProcessView = z.object({
  id: Id,
  nup: z.string(),
  processType: z.string().nullable(),
  createdOn: IsoDate.nullable(),
  /** The mining process of ours with this NUP; null = an administrative process. */
  processId: Id.nullable(),
  canonicalRef: z.string().nullable(),
  /** SEI's permanent link to the process page. */
  link: z.string().nullable(),
  interessados: z.array(z.string()),
  /** When we last read the process page (ISO timestamp); null = not read yet. */
  polledAt: z.string().nullable(),
  documents: z.array(SeiDocumentView),
  /** São Paulo wall-clock, `YYYY-MM-DDTHH:MM`, as published. */
  andamentos: z.array(z.object({ occurredAt: z.string().nullable(), unit: z.string().nullable(), description: z.string() })),
});

/**
 * The process view's SEI section: `locked` = the subscription has no SEI module; `unchecked` = not searched on SEI yet;
 * `not_on_sei` = SEI has no process with this NUP; `found` = the process as SEI publishes it.
 */
export const ProcessSeiSection = z.object({
  status: z.enum(['locked', 'unchecked', 'not_on_sei', 'found']),
  process: SeiProcessView.nullable(),
});

// the global titular filter, as AnmEntityFilter (not imported: product.ts imports this file)
const U = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
export const SeiListQuery = z.object({ anmEntity: z.string().regex(new RegExp(`^${U}(,${U})*$`, 'i')).optional() });
export const SeiListItem = z.object({
  id: Id,
  anmEntityId: Id,
  nup: z.string(),
  processType: z.string().nullable(),
  createdOn: IsoDate.nullable(),
  processId: Id.nullable(),
  canonicalRef: z.string().nullable(),
  documents: z.number().int(),
  lastDocumentOn: IsoDate.nullable(),
  /** Found only through a SEI contact that another company's processes share — the customer confirms. */
  homonym: z.boolean(),
  verdict: SeiVerdict.nullable(),
});
export const SeiListResponse = z.object({
  /** Whether any titular in view has the SEI module (false → the module's upsell, no items). */
  entitled: z.boolean(),
  anmEntities: z.array(Id),
  items: z.array(SeiListItem),
});

/** A Prazo registered by the user from a SEI document: a date, or N days/months/years from a start date (P20/P21). */
const Description = z.string().trim().min(3).max(500);
export const ManualPrazoBody = z.union([
  z.object({ description: Description, dueOn: IsoDate }),
  z.object({ description: Description, n: z.number().int().min(1).max(3650), unit: z.enum(['dias', 'meses', 'anos']), startsOn: IsoDate }),
]);
export const SeiDocumentParams = z.object({ seiDocumentId: Id });
export const ManualPrazoCreated = z.object({ id: Id, dueOn: IsoDate, dueTags: z.array(z.string()) });
/** The subscription's manual Prazos on this process's documents (open first). */
export const SeiPrazo = z.object({
  id: Id, seiDocumentId: Id, description: z.string(), dueOn: IsoDate, resolved: z.boolean(),
});

export const SeiProcessParams = z.object({ seiProcessId: Id });
export const SeiProcessResponse = z.object({
  anmEntityId: Id,
  process: SeiProcessView,
  homonym: z.boolean(),
  verdict: SeiVerdict.nullable(),
  /** Whether this login may answer the homonym question and register Prazos (WORKSPACE_WRITE). */
  canReview: z.boolean(),
  prazos: z.array(SeiPrazo),
});

export const SeiReviewBody = z.object({ verdict: SeiVerdict.nullable() });
export const SeiReviewResponse = z.object({ seiProcessId: Id, verdict: SeiVerdict.nullable() });

export type SeiDocumentView = z.infer<typeof SeiDocumentView>;
export type SeiProcessView = z.infer<typeof SeiProcessView>;
export type ProcessSeiSection = z.infer<typeof ProcessSeiSection>;
export type SeiListItem = z.infer<typeof SeiListItem>;
export type SeiListResponse = z.infer<typeof SeiListResponse>;
export type SeiProcessResponse = z.infer<typeof SeiProcessResponse>;
export type SeiVerdict = z.infer<typeof SeiVerdict>;
export type ManualPrazoBody = z.infer<typeof ManualPrazoBody>;
export type SeiPrazo = z.infer<typeof SeiPrazo>;
